import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Select from 'react-select';
import DispayAndEventsController, {
  getInitialDisplayAndEventsForm,
  initialDisplayAndEventsFilters
} from '../Controller/DispayAndEventsController';
import Loader from 'utils/Loader';
import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';

const textFieldProps = {
  InputLabelProps: {
    shrink: true
  },
  sx: {
    '& .MuiFormLabel-asterisk': {
      color: '#d32f2f'
    }
  }
};

const compactHeaderCellSx = {
  px: 1,
  py: 1,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  fontWeight: 700
};

const compactBodyCellSx = {
  px: 1,
  py: 0.75,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  verticalAlign: 'top'
};

const centeredCellSx = { textAlign: 'center' };

const visibleRadioSx = {
  p: 0.5,
  mr: 0.5,
  color: '#1976d2',
  '& .MuiSvgIcon-root': {
    color: '#1976d2'
  }
};

const radioIconSx = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid #1976d2',
  backgroundColor: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box'
};

const checkedRadioIconSx = {
  ...radioIconSx,
  '&::after': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#1976d2'
  }
};

const searchableDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : 'rgba(0, 0, 0, 0.23)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    minHeight: 40,
    padding: '0 14px'
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: 12
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1600
  })
};

const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;

const searchableFieldWrapperSx = {
  position: 'relative'
};

const searchableFieldLabelSx = {
  position: 'absolute',
  top: -8,
  left: 12,
  px: 0.5,
  backgroundColor: '#fff',
  color: 'rgba(0, 0, 0, 0.6)',
  fontSize: '0.75rem',
  lineHeight: 1,
  zIndex: 1
};

const createEventDialogSx = {
  '& .MuiDialog-container': {
    justifyContent: { xs: 'center', md: 'flex-end' }
  },
  '& .MuiDialog-paper': {
    ml: { xs: 2, md: '280px' },
    mr: { xs: 2, md: 3 },
    width: { xs: 'calc(100% - 32px)', md: 'calc(100% - 304px)' },
    maxWidth: 'none'
  }
};

const requiredFields = [
  'eventname',
  'registersrno',
  'division',
  'requestdate',
  'startdate',
  'enddate',
  'eventtype',
  'customerCode',
  'expectedsalesqty',
  'expectedsalesamount',
  'expecteddealersno',
  'expectedretailersno',
  'customeravgdaystay',
  'expectedagentsno',
  'staffavgdaystay',
  'expectedstaffno',
  'hotelcostperperson',
  'hoteltotalcost',
  'travelcostperperson',
  'traveltotalcost',
  'displaycost',
  'logisticscost',
  'photovideoshootcost',
  'totalestimatedcost',
  'expenditurepercentage',
  'expenditurepercustomer',
  'existingscheme',
  'additionalscheme',
  'schemerate',
  'schemefrom',
  'schemeupto'
];

const numericFields = [
  'expectedsalesqty',
  'expectedsalesamount',
  'expecteddealersno',
  'expectedretailersno',
  'customeravgdaystay',
  'expectedagentsno',
  'staffavgdaystay',
  'expectedstaffno',
  'hotelcostperperson',
  'hoteltotalcost',
  'travelcostperperson',
  'traveltotalcost',
  'displaycost',
  'logisticscost',
  'photovideoshootcost',
  'totalestimatedcost',
  'expenditurepercentage',
  'expenditurepercustomer',
  'schemerate',
  'dresscostperperson',
  'dressstaffcount',
  'dressstafftotalcost',
  'dressagentscount',
  'dressagentstotalcost',
  'giftcostperitem',
  'giftquantity',
  'gifttotalcost'
];

const moneyFields = [
  'expectedsalesamount',
  'hotelcostperperson',
  'hoteltotalcost',
  'travelcostperperson',
  'traveltotalcost',
  'displaycost',
  'logisticscost',
  'photovideoshootcost',
  'totalestimatedcost',
  'expenditurepercustomer',
  'dresscostperperson',
  'dressstafftotalcost',
  'dressagentstotalcost',
  'giftcostperitem',
  'gifttotalcost'
];

const dateFields = ['requestdate', 'startdate', 'enddate', 'schemefrom', 'schemeupto'];

const statusFilterOptions = [
  { value: 'Request Submitted', label: 'Request Submitted' },
  { value: 'ASM Approved', label: 'ASM Approved' },
  { value: 'ZSM Approved', label: 'ZSM Approved' },
  { value: 'Brand Head Approved', label: 'Brand Head Approved' },
  { value: 'ASM Rejected', label: 'ASM Rejected' },
  { value: 'ZSM Rejected', label: 'ZSM Rejected' },
  { value: 'Brand Head Rejected', label: 'Brand Head Rejected' }
];

const createAgencyRow = () => ({
  agencyType: '',
  purpose: '',
  amount: ''
});

const basicInformationFields = [
  { name: 'division', label: 'Division', placeholder: 'Search Division' },
  { name: 'registersrno', label: 'Register Sr. No.', placeholder: 'REG002' },
  { name: 'requestdate', label: 'Request Date', type: 'date' },
  { name: 'eventname', label: 'Event Name', placeholder: 'Retail Display Event Mumbai' },
  { name: 'startdate', label: 'Start Date', type: 'date' },
  { name: 'enddate', label: 'End Date', type: 'date' },
  { name: 'eventtype', label: 'Event Type', placeholder: 'Retail Display' },
  { name: 'customerCode', label: 'Customer', placeholder: 'Search Customer', md: 8 }
];

const expectedSalesFields = [
  { name: 'expectedsalesqty', label: 'Qty', placeholder: '1000' },
  { name: 'expectedsalesamount', label: 'Amount', placeholder: '5000000' }
];

const expectedCustomerFields = [
  { name: 'expecteddealersno', label: 'No. of Dealers', placeholder: '40' },
  { name: 'expectedretailersno', label: 'No. of Retailers', placeholder: '100' },
  { name: 'customeravgdaystay', label: 'Avg. Days Stay Per Visitor', placeholder: '3' }
];

const expectedStaffFields = [
  { name: 'expectedagentsno', label: 'No. of Agents', placeholder: '15' },
  { name: 'expectedstaffno', label: 'No. of Staff', placeholder: '10' },
  { name: 'staffavgdaystay', label: 'Avg. Days Stay Per Visitor', placeholder: '3' }
];

const hotelCostFields = [
  { name: 'hotelcostperperson', label: 'Cost Per Person / Day', placeholder: '3000' },
  { name: 'hoteltotalcost', label: 'Total Cost' }
];

const travelCostFields = [
  { name: 'travelcostperperson', label: 'Cost Per Person', placeholder: '1200' },
  { name: 'traveltotalcost', label: 'Total Cost' }
];

const otherCostFields = [
  { name: 'displaycost', label: '3. Cost of arranging display at venue', placeholder: '50000' },
  { name: 'logisticscost', label: '4. Cost of Logistics', placeholder: '25000' },
  { name: 'photovideoshootcost', label: '5. Cost of Photo & Video Shoot', placeholder: '15000' }
];

const summaryFields = [
  { name: 'totalestimatedcost', label: 'Total Estimated Expenditure' },
  { name: 'expenditurepercentage', label: 'Expenditure %' },
  { name: 'expenditurepercustomer', label: 'Expenditure Per Customer' }
];

const schemeDetailFields = [
  { name: 'schemerate', label: 'Rate of Scheme (%)', placeholder: '2' },
  { name: 'schemefrom', label: 'Applicable From', type: 'date' },
  { name: 'schemeupto', label: 'Applicable Upto', type: 'date' }
];

const giftFields = [
  { name: 'giftitem', label: 'Gift Item', md: 6 },
  { name: 'giftcostperitem', label: 'Cost Per Item', md: 6 },
  { name: 'giftquantity', label: 'No Of Gifts To Be Distributed', md: 6 },
  { name: 'gifttotalcost', label: 'Total Cost', md: 6 }
];

const sanitizeNumericInput = (value) => {
  const cleanedValue = String(value || '').replace(/[^\d.]/g, '');
  const [wholePart, ...decimalParts] = cleanedValue.split('.');

  return decimalParts.length ? `${wholePart}.${decimalParts.join('')}` : wholePart;
};

const formatDisplayDate = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '-';
  }

  const parsedDate = new Date(normalizedValue.includes(' ') ? normalizedValue.replace(' ', 'T') : `${normalizedValue}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatHistoryDateTime = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '-';
  }

  const parsedDate = new Date(normalizedValue.includes(' ') ? normalizedValue.replace(' ', 'T') : normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatAmount = (value) => {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue)) {
    return value || '-';
  }

  return numericValue.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: String(value || '').includes('.') ? 2 : 0
  });
};

const getStatusTextSx = (status) => {
  const normalizedStatus = String(status || '').toLowerCase();

  if (normalizedStatus.includes('approved')) {
    return {
      color: '#1b5e20',
      fontWeight: 700
    };
  }

  if (normalizedStatus.includes('rejected')) {
    return {
      color: '#d2011d',
      fontWeight: 700
    };
  }

  return {
    color: '#b26a00',
    fontWeight: 700
  };
};

const DispayAndEvents = () => {
  const [page, setPage] = useState(0);
  const {
    actionHistory,
    agencyPurposeOptions,
    clearActionHistory,
    customerOptions,
    divisionOptions,
    eventRequests,
    filters,
    historyError,
    isAgencyPurposeOptionsLoading,
    isDivisionOptionsLoading,
    isHistoryLoading,
    isCustomerOptionsLoading,
    isSubmitting,
    isTableLoading,
    loggedInUser,
    loadActionHistory,
    setFilters,
    submitEventRequest,
    totalRows
  } = DispayAndEventsController(page);
  const initialForm = useMemo(() => getInitialDisplayAndEventsForm(loggedInUser), [loggedInUser]);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEventRequest, setSelectedEventRequest] = useState(null);
  const [selectedActionHistoryEvent, setSelectedActionHistoryEvent] = useState(null);
  const [selectedDivisionOption, setSelectedDivisionOption] = useState(null);
  const [agencyRows, setAgencyRows] = useState([createAgencyRow()]);

  const resolvedDivisionOption = useMemo(
    () => divisionOptions.find((option) => option.value === formData.division)
      || selectedDivisionOption
      || (formData.division ? { value: formData.division, label: formData.division } : null),
    [divisionOptions, formData.division, selectedDivisionOption]
  );

  const resolvedCustomerOption = useMemo(
    () => customerOptions.find((option) => option.value === formData.customerCode) || null,
    [customerOptions, formData.customerCode]
  );

  const resolvedFilterDivisionOption = useMemo(
    () => divisionOptions.find((option) => option.value === filters.division || option.apiValue === filters.division) || null,
    [divisionOptions, filters.division]
  );

  const resolvedFilterStatusOption = useMemo(
    () => statusFilterOptions.find((option) => option.value === filters.status) || null,
    [filters.status]
  );

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  /*
  useEffect(() => {
    const customerCount = toNumber(formData.expecteddealersno) + toNumber(formData.expectedretailersno);
    const giftTotal = toNumber(formData.giftcostperitem) * toNumber(formData.giftquantity);
    const totalEstimated = toNumber(formData.hoteltotalcost)
      + toNumber(formData.traveltotalcost)
      + toNumber(formData.displaycost)
      + toNumber(formData.logisticscost)
      + toNumber(formData.photovideoshootcost);
    const expectedSalesAmount = toNumber(formData.expectedsalesamount);

    const nextCalculatedValues = {
      totalestimatedcost: formatDecimal(totalEstimated),
      expenditurepercentage: expectedSalesAmount > 0 ? formatDecimal((totalEstimated / expectedSalesAmount) * 100) : '',
      expenditurepercustomer: customerCount > 0 ? formatDecimal(totalEstimated / customerCount) : '',
      gifttotalcost: formatDecimal(giftTotal)
    };

    setFormData((prev) => {
      const hasChanges = Object.entries(nextCalculatedValues).some(([key, value]) => prev[key] !== value);
      return hasChanges ? { ...prev, ...nextCalculatedValues } : prev;
    });
  }, [
    formData.displaycost,
    formData.expectedagentsno,
    formData.expecteddealersno,
    formData.expectedretailersno,
    formData.expectedstaffno,
    formData.expectedsalesamount,
    formData.giftcostperitem,
    formData.giftquantity,
    formData.hoteltotalcost,
    formData.logisticscost,
    formData.photovideoshootcost,
    formData.traveltotalcost
  ]);
  */

  const handleOpenCreateDialog = () => {
    setFormData(initialForm);
    setSelectedDivisionOption(null);
    setAgencyRows([createAgencyRow()]);
    setErrors({});
    setShowCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    if (isSubmitting) {
      return;
    }

    setShowCreateDialog(false);
    setSelectedDivisionOption(null);
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};
    const currentDivisionValue = resolvedDivisionOption?.value || formData.division;

    requiredFields.forEach((fieldName) => {
      if (!String(formData[fieldName] || '').trim()) {
        nextErrors[fieldName] = 'Field is Required';
      }
    });

    if (!String(currentDivisionValue || '').trim()) {
      nextErrors.division = 'Field is Required';
    }

    numericFields.forEach((fieldName) => {
      const value = String(formData[fieldName] || '').trim();

      if (value && Number.isNaN(Number(value))) {
        nextErrors[fieldName] = 'Enter a valid number';
      }
    });

    if (formData.startdate && formData.enddate && new Date(formData.startdate) > new Date(formData.enddate)) {
      nextErrors.enddate = 'End date cannot be before start date';
    }

    agencyRows.forEach((row, index) => {
      ['agencyType', 'purpose', 'amount'].forEach((fieldName) => {
        if (!String(row[fieldName] || '').trim()) {
          nextErrors[`agencyRows.${index}.${fieldName}`] = 'Fields are Required';
        }
      });

      if (String(row.amount || '').trim() && Number.isNaN(Number(row.amount))) {
        nextErrors[`agencyRows.${index}.amount`] = 'Enter a valid number';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    const nextValue = numericFields.includes(name) ? sanitizeNumericInput(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleDivisionChange = (selectedOption) => {
    setSelectedDivisionOption(selectedOption);
    setFormData((prev) => ({
      ...prev,
      division: selectedOption?.value || ''
    }));
    setErrors((prev) => ({
      ...prev,
      division: ''
    }));
  };

  const handleCustomerChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      customerCode: selectedOption?.value || ''
    }));
    setErrors((prev) => ({
      ...prev,
      customerCode: ''
    }));
  };

  const handleAgencyRowChange = (rowIndex, fieldName, value) => {
    const nextValue = fieldName === 'amount' ? sanitizeNumericInput(value) : value;

    setAgencyRows((prev) => prev.map((row, index) => (
      index === rowIndex ? { ...row, [fieldName]: nextValue } : row
    )));

    setErrors((prev) => ({
      ...prev,
      [`agencyRows.${rowIndex}.${fieldName}`]: ''
    }));
  };

  const validateAgencyRows = () => {
    const nextAgencyErrors = {};

    agencyRows.forEach((row, index) => {
      ['agencyType', 'purpose', 'amount'].forEach((fieldName) => {
        if (!String(row[fieldName] || '').trim()) {
          nextAgencyErrors[`agencyRows.${index}.${fieldName}`] = 'Fields are Required';
        }
      });

      if (String(row.amount || '').trim() && Number.isNaN(Number(row.amount))) {
        nextAgencyErrors[`agencyRows.${index}.amount`] = 'Enter a valid number';
      }
    });

    setErrors((prev) => ({
      ...prev,
      ...nextAgencyErrors
    }));

    return Object.keys(nextAgencyErrors).length === 0;
  };

  const handleAddAgencyRow = () => {
    if (!validateAgencyRows()) {
      showAlert('Please fill all agency row fields before adding another row.');
      return;
    }

    setAgencyRows((prev) => [...prev, createAgencyRow()]);
  };

  const handleDeleteAgencyRow = (rowIndex) => {
    if (rowIndex === 0) {
      return;
    }

    setAgencyRows((prev) => prev.filter((row, index) => index !== rowIndex));
    setErrors((prev) => {
      const nextErrors = {};

      Object.entries(prev).forEach(([key, value]) => {
        const match = key.match(/^agencyRows\.(\d+)\.(.+)$/);

        if (!match) {
          nextErrors[key] = value;
          return;
        }

        const errorRowIndex = Number(match[1]);
        const fieldName = match[2];

        if (errorRowIndex < rowIndex) {
          nextErrors[key] = value;
        } else if (errorRowIndex > rowIndex) {
          nextErrors[`agencyRows.${errorRowIndex - 1}.${fieldName}`] = value;
        }
      });

      return nextErrors;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterFieldChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleFilterDivisionChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      division: selectedOption?.apiValue || ''
    }));
    setPage(0);
  };

  const handleFilterStatusChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      status: selectedOption?.value || ''
    }));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(initialDisplayAndEventsFilters);
    setPage(0);
  };

  const handleOpenActionHistory = (request) => {
    setSelectedActionHistoryEvent(request);
    loadActionHistory(request?.eventid);
  };

  const handleCloseActionHistory = () => {
    setSelectedActionHistoryEvent(null);
    clearActionHistory();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      showAlert('Please fill all mandatory fields correctly.');
      return;
    }

    try {
      const response = await submitEventRequest({
        ...formData,
        division: selectedDivisionOption?.apiValue || formData.division,
        agencyRows: agencyRows.filter((row) => (
          String(row.agencyType || '').trim()
          || String(row.purpose || '').trim()
          || String(row.amount || '').trim()
        ))
      });
      await showSuccessAlert(response?.msg || 'Request Submitted Successfully', 'Success');
      setShowCreateDialog(false);
      setFormData(initialForm);
      setSelectedDivisionOption(null);
      setAgencyRows([createAgencyRow()]);
    } catch (error) {
      showAlert(error?.message || 'Failed to submit event request.');
    }
  };

  const renderField = (field) => (
    <Grid item xs={12} sm={6} md={field.md || 4} key={field.name}>
      {field.name === 'division' ? (
        <>
          <Box sx={searchableFieldWrapperSx}>
            <Typography variant="body2" sx={searchableFieldLabelSx}>
              {field.label} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Select
              value={resolvedDivisionOption}
              placeholder={field.placeholder}
              isSearchable
              isClearable
              isLoading={isDivisionOptionsLoading}
              options={divisionOptions}
              styles={searchableDropdownStyles}
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition="fixed"
              noOptionsMessage={() => 'No data found'}
              onChange={handleDivisionChange}
            />
          </Box>
          {errors.division ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
              {errors.division}
            </Typography>
          ) : null}
        </>
      ) : field.name === 'customerCode' ? (
        <>
          <Box sx={searchableFieldWrapperSx}>
            <Typography variant="body2" sx={searchableFieldLabelSx}>
              {field.label} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Select
              value={resolvedCustomerOption}
              placeholder={field.placeholder}
              isSearchable
              isClearable
              isLoading={isCustomerOptionsLoading}
              options={customerOptions}
              styles={searchableDropdownStyles}
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition="fixed"
              noOptionsMessage={() => 'No data found'}
              onChange={handleCustomerChange}
            />
          </Box>
          {errors.customerCode ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
              {errors.customerCode}
            </Typography>
          ) : null}
        </>
      ) : (
        <TextField
          required={requiredFields.includes(field.name)}
          fullWidth
          label={field.label}
          name={field.name}
          type={field.type || 'text'}
          value={formData[field.name] || ''}
          onChange={handleFieldChange}
          error={Boolean(errors[field.name])}
          helperText={errors[field.name] || (field.name === 'expenditurepercustomer' ? 'Note (Total expenditure/ no. of dealers or retailers visiting)' : '')}
          placeholder={field.placeholder || ''}
          inputProps={{
            inputMode: numericFields.includes(field.name) ? 'decimal' : undefined,
            readOnly: field.readOnly
          }}
          {...textFieldProps}
        />
      )}
    </Grid>
  );

  const renderSectionTitle = (title, subtitle) => (
    <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );

  const renderSectionCard = (title, children, subtitle) => (
    <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)' }}>
      {renderSectionTitle(title, subtitle)}
      {children}
    </Card>
  );

  const renderFieldGroup = (heading, fields) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {heading}
      </Typography>
      <Grid container spacing={2}>
        {fields.map(renderField)}
      </Grid>
    </Box>
  );

  const renderRadioGroup = (name, label, options) => (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      <FormLabel component="legend" error={Boolean(errors[name])} sx={{ mb: 0.75, color: 'text.primary', fontWeight: 600 }}>
        {label} {requiredFields.includes(name) ? <span style={{ color: '#d32f2f' }}>*</span> : null}
      </FormLabel>
      <RadioGroup row name={name} value={formData[name] || ''} onChange={handleFieldChange} sx={{ gap: 2 }}>
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;

          return (
            <FormControlLabel
              key={optionValue}
              value={optionValue}
              control={(
                <Radio
                  size="small"
                  sx={visibleRadioSx}
                  icon={<Box component="span" sx={radioIconSx} />}
                  checkedIcon={<Box component="span" sx={checkedRadioIconSx} />}
                />
              )}
              label={optionLabel}
              sx={{
                mr: 0,
                color: 'text.primary',
                '& .MuiFormControlLabel-label': {
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          );
        })}
      </RadioGroup>
      {errors[name] ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {errors[name]}
        </Typography>
      ) : null}
    </FormControl>
  );

  const renderAgencyTable = () => (
    <Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Type of Agency <span style={{ color: '#d32f2f' }}>*</span></TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Purpose <span style={{ color: '#d32f2f' }}>*</span></TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Amount <span style={{ color: '#d32f2f' }}>*</span></TableCell>
              <TableCell sx={{ fontWeight: 700, width: 72, textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agencyRows.map((row, index) => {
              const selectedPurposeOption = agencyPurposeOptions.find((option) => option.value === row.purpose) || null;

              return (
                <TableRow key={`agency-row-${index}`}>
                  <TableCell>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      value={row.agencyType}
                      onChange={(event) => handleAgencyRowChange(index, 'agencyType', event.target.value)}
                      placeholder="Agency type"
                      error={Boolean(errors[`agencyRows.${index}.agencyType`])}
                      helperText={errors[`agencyRows.${index}.agencyType`]}
                      {...textFieldProps}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={selectedPurposeOption}
                      placeholder="Select Purpose"
                      isSearchable
                      isClearable
                      isLoading={isAgencyPurposeOptionsLoading}
                      options={agencyPurposeOptions}
                      styles={searchableDropdownStyles}
                      menuPortalTarget={selectMenuPortalTarget}
                      menuPosition="fixed"
                      noOptionsMessage={() => 'No data found'}
                      onChange={(selectedOption) => handleAgencyRowChange(index, 'purpose', selectedOption?.value || '')}
                    />
                    {errors[`agencyRows.${index}.purpose`] ? (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
                        {errors[`agencyRows.${index}.purpose`]}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      value={row.amount}
                      onChange={(event) => handleAgencyRowChange(index, 'amount', event.target.value)}
                      placeholder="Amount"
                      error={Boolean(errors[`agencyRows.${index}.amount`])}
                      helperText={errors[`agencyRows.${index}.amount`]}
                      inputProps={{ inputMode: 'decimal' }}
                      {...textFieldProps}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {index > 0 ? (
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete agency row"
                        onClick={() => handleDeleteAgencyRow(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddAgencyRow}
        sx={{ mt: 1.5, textTransform: 'none' }}
      >
        Add More
      </Button>
    </Box>
  );

  const renderDressCodeTable = () => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>No.</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Total Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            { label: 'Staff', count: 'dressstaffcount', total: 'dressstafftotalcost' },
            { label: 'Agents', count: 'dressagentscount', total: 'dressagentstotalcost' }
          ].map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  name={row.count}
                  value={formData[row.count] || ''}
                  onChange={handleFieldChange}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  name={row.total}
                  value={formData[row.total] || ''}
                  onChange={handleFieldChange}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const getDetailFieldValue = (field) => {
    const value = selectedEventRequest?.[field.name];

    if (field.name === 'existingscheme' || field.name === 'additionalscheme') {
      if (String(value) === '1') {
        return 'Applicable';
      }

      if (String(value) === '0') {
        return 'Not Applicable';
      }
    }

    if (dateFields.includes(field.name)) {
      return formatDisplayDate(value);
    }

    if (moneyFields.includes(field.name)) {
      return formatAmount(value);
    }

    return value || '';
  };

  const renderDetailField = (field) => (
    <Grid item xs={12} sm={6} md={field.md || 4} key={`detail-${field.name}`}>
      <TextField
        fullWidth
        label={field.label}
        value={getDetailFieldValue(field)}
        InputProps={{ readOnly: true }}
        {...textFieldProps}
      />
    </Grid>
  );

  const renderDetailFieldGroup = (heading, fields) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {heading}
      </Typography>
      <Grid container spacing={2}>
        {fields.map(renderDetailField)}
      </Grid>
    </Box>
  );

  const renderDetailDressCodeTable = () => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>No.</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Total Cost</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            { label: 'Staff', count: 'dressstaffcount', total: 'dressstafftotalcost' },
            { label: 'Agents', count: 'dressagentscount', total: 'dressagentstotalcost' }
          ].map((row) => (
            <TableRow key={`detail-${row.label}`}>
              <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
              <TableCell>
                <TextField fullWidth size="small" value={selectedEventRequest?.[row.count] || ''} InputProps={{ readOnly: true }} />
              </TableCell>
              <TableCell>
                <TextField fullWidth size="small" value={formatAmount(selectedEventRequest?.[row.total])} InputProps={{ readOnly: true }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderDetailAgencyTable = () => {
    const agencies = Array.isArray(selectedEventRequest?.agencies) ? selectedEventRequest.agencies : [];

    return (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Type of Agency</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Purpose</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agencies.length > 0 ? (
              agencies.map((agency, index) => (
                <TableRow key={agency.agencyid || `detail-agency-${index}`}>
                  <TableCell>
                    <TextField fullWidth size="small" value={agency.agencytype || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth size="small" value={agency.purpose || ''} InputProps={{ readOnly: true }} {...textFieldProps} />
                  </TableCell>
                  <TableCell>
                    <TextField fullWidth size="small" value={formatAmount(agency.amount)} InputProps={{ readOnly: true }} {...textFieldProps} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        minHeight: '100%',
        padding: '0 12px 28px'
      }}
    >
      {isSubmitting ? (
        <div className="overlay">
          <Loader />
          <span style={{ marginLeft: '10px' }}>Submitting event request...</span>
        </div>
      ) : null}

      <div className="cutrange" style={{ width: '100%', maxWidth: '100%' }}>
        <span className="font20">DISPLAY AND EVENTS</span>
      </div>

      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          p: { xs: 1.5, md: 3, xl: 4 },
          borderRadius: 2.5,
          position: 'relative'
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5">Display and Events List</Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Total Rows: {totalRows}
            </Typography> */}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            size="small"
            sx={{
              textTransform: 'none',
              alignSelf: { xs: 'flex-end', sm: 'center' },
              width: 'fit-content',
              minWidth: 170,
              ml: { xs: 'auto', sm: 0 },
              whiteSpace: 'nowrap',
              px: 1.75,
              py: 0.75,
              flexShrink: 0
            }}
          >
            Create Event Request
          </Button>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Event Name"
              name="eventname"
              value={filters.eventname}
              onChange={handleFilterFieldChange}
              placeholder="Search event name"
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={searchableFieldWrapperSx}>
              <Typography variant="body2" sx={searchableFieldLabelSx}>
                Division
              </Typography>
              <Select
                value={resolvedFilterDivisionOption}
                placeholder="Select Division"
                isSearchable
                isClearable
                isLoading={isDivisionOptionsLoading}
                options={divisionOptions}
                styles={searchableDropdownStyles}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                noOptionsMessage={() => 'No data found'}
                onChange={handleFilterDivisionChange}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="fromDate"
              type="date"
              value={filters.fromDate}
              onChange={handleFilterFieldChange}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="toDate"
              type="date"
              value={filters.toDate}
              onChange={handleFilterFieldChange}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={2}>
            <Box sx={searchableFieldWrapperSx}>
              <Typography variant="body2" sx={searchableFieldLabelSx}>
                Status
              </Typography>
              <Select
                value={resolvedFilterStatusOption}
                placeholder="Select Status"
                isSearchable
                isClearable
                options={statusFilterOptions}
                styles={searchableDropdownStyles}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                noOptionsMessage={() => 'No data found'}
                onChange={handleFilterStatusChange}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} md={12} lg={1}>
            <Button fullWidth variant="outlined" size="small" onClick={handleResetFilters} sx={{ minHeight: 40, textTransform: 'none' }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ position: 'relative' }}>
          {isTableLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <Loader />
            </Box>
          ) : null}
          <TableContainer
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(224, 224, 224, 0.8)',
              borderRadius: 2
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                tableLayout: 'auto',
                '& .MuiTableCell-root': {
                  borderBottomColor: 'rgba(224, 224, 224, 0.8)'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={compactHeaderCellSx}>Event No</TableCell>
                  <TableCell sx={compactHeaderCellSx}>Event Name</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Division</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Event Type</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Request Date</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Event Dates</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Expected Sales Amt</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Total Cost</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Status</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventRequests.length > 0 ? (
                  eventRequests.map((request) => (
                    <TableRow key={request.eventid || request.createdat} hover>
                      <TableCell sx={compactBodyCellSx}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setSelectedEventRequest(request)}
                          sx={{
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: 0,
                            justifyContent: 'flex-start',
                            color: '#1976d2',
                            textDecoration: 'underline',
                            fontWeight: 600
                          }}
                        >
                          {request.showid || '-'}
                        </Button>
                      </TableCell>
                      <TableCell sx={compactBodyCellSx}>{request.eventname || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{request.division || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{request.eventtype || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatDisplayDate(request.requestdate)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        {formatDisplayDate(request.startdate)} - {formatDisplayDate(request.enddate)}
                      </TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatAmount(request.expectedsalesamount)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatAmount(request.totalestimatedcost)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        <Box component="span" sx={{ fontSize: '0.72rem', ...getStatusTextSx(request.status) }}>
                          {request.status || '-'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenActionHistory(request)}
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid rgba(25, 118, 210, 0.35)',
                            color: '#1976d2',
                            '&:hover': {
                              borderColor: '#1976d2',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                          }}
                          title="History"
                        >
                          <HistoryOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            rowsPerPage={50}
            rowsPerPageOptions={[]}
            onPageChange={handleChangePage}
          />
        </Box>
      </Card>

      <Dialog
        open={showCreateDialog}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleCloseCreateDialog();
        }}
        fullWidth
        maxWidth="lg"
        sx={createEventDialogSx}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>Create Event Request</DialogTitle>
          <DialogContent sx={{ backgroundColor: '#fafafa' }}>
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              {renderSectionCard(
                'Basic Information',
                (
                  <Grid container spacing={2}>
                    {basicInformationFields.map(renderField)}
                  </Grid>
                )
              )}

              {renderSectionCard(
                'Estimates on Revenue & Visitors',
                (
                  <Box>
                    {renderFieldGroup('1. Expected Sales', expectedSalesFields)}
                    {renderFieldGroup('2. Expected Number of Customers', expectedCustomerFields)}
                    {renderFieldGroup('3. Expected Number of Staff & Agents', expectedStaffFields)}
                  </Box>
                )
              )}

              {renderSectionCard(
                'Estimates on Expenditure',
                (
                  <Box>
                    {renderFieldGroup('1. Expected Hotel / Resort / Property Cost', hotelCostFields)}
                    {renderFieldGroup('2. Expected Travel Cost', travelCostFields)}
                    {renderFieldGroup('Other Costs', otherCostFields)}
                  </Box>
                )
              )}

              {renderSectionCard(
                'Details on Other Agencies Involved',
                renderAgencyTable()
              )}

              {renderSectionCard(
                'Details on Scheme',
                (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        {renderRadioGroup('existingscheme', '1. Existing Scheme', [
                          { label: 'Applicable', value: '1' },
                          { label: 'Not Applicable', value: '0' }
                        ])}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderRadioGroup(
                          'additionalscheme',
                          '2. Additional Conference Scheme / Discount',
                          [
                            { label: 'Applicable', value: '1' },
                            { label: 'Not Applicable', value: '0' }
                          ]
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      {schemeDetailFields.map(renderField)}
                    </Grid>
                  </Box>
                )
              )}

              {renderSectionCard(
                'Others',
                (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Dress Code For Events (If Applicable)
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {renderField({ name: 'dresscostperperson', label: 'Cost Per Person' })}
                    </Grid>
                    {renderDressCodeTable()}

                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
                      Gift For Visitors (If Providing)
                    </Typography>
                    <Grid container spacing={2}>
                      {giftFields.map(renderField)}
                    </Grid>
                  </Box>
                )
              )}

              {renderSectionCard(
                'Summary',
                (
                  <Grid container spacing={2}>
                    {summaryFields.map(renderField)}
                  </Grid>
                )
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} sx={{ textTransform: 'none' }} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ textTransform: 'none' }} disabled={isSubmitting}>
              Submit Request
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(selectedEventRequest)} onClose={() => setSelectedEventRequest(null)} fullWidth maxWidth="lg">
        <DialogTitle>Event Request Details</DialogTitle>
        <DialogContent sx={{ backgroundColor: '#fafafa' }}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {renderSectionCard(
              'Basic Information',
              (
                <Grid container spacing={2}>
                  {basicInformationFields.map(renderDetailField)}
                </Grid>
              )
            )}

            {renderSectionCard(
              'Estimates on Revenue & Visitors',
              (
                <Box>
                  {renderDetailFieldGroup('1. Expected Sales', expectedSalesFields)}
                  {renderDetailFieldGroup('2. Expected Number of Customers', expectedCustomerFields)}
                  {renderDetailFieldGroup('3. Expected Number of Staff & Agents', expectedStaffFields)}
                </Box>
              )
            )}

            {renderSectionCard(
              'Estimates on Expenditure',
              (
                <Box>
                  {renderDetailFieldGroup('1. Expected Hotel / Resort / Property Cost', hotelCostFields)}
                  {renderDetailFieldGroup('2. Expected Travel Cost', travelCostFields)}
                  {renderDetailFieldGroup('Other Costs', otherCostFields)}
                </Box>
              )
            )}

            {renderSectionCard(
              'Details on Other Agencies Involved',
              renderDetailAgencyTable()
            )}

            {renderSectionCard(
              'Details on Scheme',
              (
                <Box>
                  <Grid container spacing={2}>
                    {[
                      { name: 'existingscheme', label: '1. Existing Scheme' },
                      { name: 'additionalscheme', label: '2. Additional Conference Scheme / Discount' }
                    ].map(renderDetailField)}
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {schemeDetailFields.map(renderDetailField)}
                  </Grid>
                </Box>
              )
            )}

            {renderSectionCard(
              'Others',
              (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Dress Code For Events (If Applicable)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {renderDetailField({ name: 'dresscostperperson', label: 'Cost Per Person' })}
                  </Grid>
                  {renderDetailDressCodeTable()}

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
                    Gift For Visitors (If Providing)
                  </Typography>
                  <Grid container spacing={2}>
                    {giftFields.map(renderDetailField)}
                  </Grid>
                </Box>
              )
            )}

            {renderSectionCard(
              'Summary',
              (
                <Grid container spacing={2}>
                  {summaryFields.map(renderDetailField)}
                </Grid>
              )
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedEventRequest(null)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedActionHistoryEvent)} onClose={handleCloseActionHistory} fullWidth maxWidth="lg">
        <DialogTitle>Action History</DialogTitle>
        <DialogContent>
          {isHistoryLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Loading history...
            </Typography>
          ) : historyError ? (
            <Typography variant="body2" color="error" sx={{ pt: 1 }}>
              {historyError}
            </Typography>
          ) : actionHistory.length > 0 ? (
            <TableContainer sx={{ pt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Action By</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actionHistory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.action || '-'}</TableCell>
                      <TableCell>{item.actionBy || '-'}</TableCell>
                      <TableCell>{item.role || '-'}</TableCell>
                      <TableCell>
                        <Box component="span" sx={{ fontSize: '0.72rem', ...getStatusTextSx(item.status) }}>
                          {item.status || '-'}
                        </Box>
                      </TableCell>
                      {/* <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.remarks || '-'}</TableCell> */}
                      <TableCell>{formatHistoryDateTime(item.actionDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              No history found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseActionHistory} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DispayAndEvents;
